import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MdEdit, MdDelete } from 'react-icons/md';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import MultiSelectSearchableDropdown from '../Subscription/MultiSelectSearchableDropdown';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import CustomDatePicker from '../Common/CustomDatePicker';
import { toast } from 'react-toastify';
import { getAllPOSList, saveOrganization, getOrgMembersByOrgId } from '../../Services/OrganizationService';
import { useAuth } from "../../Context/AuthContext";
import { isSuperAdminUser } from '../../Utils/Permissions';
import { getAllClientNames } from '../../Services/ClientService';
import CustomPhoneInput from '../Common/CustomPhoneInput';

// statuses.id values, status_type 14 = "Internal User Type"
const USER_TYPE_BILLING_MEMBER = 78;
const USER_TYPE_CREDENTIALING_MEMBER = 79;

// Validation schema
const validationSchema = Yup.object({
  groupOfficeName: Yup.string()
    .required('Group Office Name is required')
    .min(2, 'Must be at least 2 characters'),
  dba: Yup.string()
    .nullable()
    .notRequired()
    .min(2, 'Must be at least 2 characters'),
  phone: Yup.string()
    .required('Phone Number is required')
    .test("valid-phone", "Please enter a valid phone number",
      (value) => {
        if (!value) return false;
        // Remove spaces, brackets, hyphens, etc.
        const digits = value.replace(/\D/g, "");
        // Must contain at least 10 digits
        return digits.length >= 10;
      }
    ),
  cid: Yup.string()
    .required('CID is required')
    .length(5, 'CID must be exactly 5 characters'),
  email: Yup.string().required('Email is required'),
  taxId: Yup.string().required('Tax ID is required'),
  groupNpi: Yup.string()
    .required('Group NPI is required')
    .matches(/^\d{10}$/, 'NPI must be 10 digits'),
  mailingAddress: Yup.object({
    street: Yup.string().required('Address Line 1 is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    zipcode: Yup.string()
      .required('ZIP Code is required')
  }),
  account: Yup.mixed().nullable().required('Account is required'),
  payToAddress: Yup.object({
    street: Yup.string().required('Address Line 1 is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    zipcode: Yup.string()
      .required('ZIP Code is required')
  }),
  drugInjectionNotes: Yup.string().when('drugInjection', {
    is: true,
    then: (schema) => schema.required('Drug Injection Notes are required'),
    otherwise: (schema) => schema,
  }),
});

const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

const formatIdentifierInput = (value, type) => {
  const digits = normalizeDigits(value);
  if (!digits) return '';

  if (type === 'taxId') {
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
  }

  if (type === 'npi') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  if (type === 'fax') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  return value;
};

const resolveIsCredentialing = (data) => {
  if (!data) return false;
  const rawVal = data.is_credentialing ?? data.IsCredentialing ?? data.is_credential ?? data.IsCredential ?? data.is_credientailing ?? data.isCredientailing ?? data.isCredentialing;
  if (typeof rawVal === 'boolean') return rawVal;
  if (typeof rawVal === 'string') {
    if (rawVal.toLowerCase() === 'true' || rawVal === '1') return true;
    if (rawVal.toLowerCase() === 'false' || rawVal === '0') return false;
  }
  if (typeof rawVal === 'number') return rawVal === 1;
  const rawCred = data.cred_mem ?? data.CredMem ?? data.handled_by ?? data.HandledBy ?? data.credMembers ?? data.cred_member ?? data.CredMember;
  if (Array.isArray(rawCred)) return rawCred.length > 0;
  if (rawCred && typeof rawCred === 'object') return Object.keys(rawCred).length > 0;
  if (typeof rawCred === 'number') return true;
  if (typeof rawCred === 'string') return rawCred.trim().length > 0;
  return false;
};

const resolveCredMember = (data) => {
  if (!data) return '';
  const rawCred = data.cred_mem ?? data.CredMem ?? data.handled_by ?? data.HandledBy ?? data.credMembers ?? data.cred_member ?? data.CredMember ?? data.handledBy;
  if (Array.isArray(rawCred)) {
    if (rawCred.length === 0) return '';
    const first = rawCred[0];
    if (first == null) return '';
    if (typeof first === 'object') {
      return String(first.value ?? first.id ?? first.Id ?? first.member_id ?? first.MemberId ?? first.userId ?? first.UserId ?? first.handled_by ?? '');
    }
    return String(first);
  }
  if (rawCred && typeof rawCred === 'object') {
    return String(rawCred.value ?? rawCred.id ?? rawCred.Id ?? rawCred.member_id ?? rawCred.MemberId ?? rawCred.userId ?? rawCred.UserId ?? rawCred.handled_by ?? rawCred.handledBy ?? '');
  }
  if (typeof rawCred === 'number' || typeof rawCred === 'string') {
    return String(rawCred);
  }
  return '';
};

export default function ClientProfile({ onSaveSuccess, onContinue, isEditMode = false, initialData = null, organizationId = null }) {
  const navigate = useNavigate();
  const { token, decodedToken } = useAuth();
  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const accountId = decodedToken?.account_id;
  const [sameAsMailingAddress, setSameAsMailingAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTaxonomyModal, setShowTaxonomyModal] = useState(false);
  const [editingTaxonomyIndex, setEditingTaxonomyIndex] = useState(null);
  const [taxonomyForm, setTaxonomyForm] = useState({
    code: '',
    desc: '',
    license: '',
    primary: false,
    custom: false,
    state: '',
    name: '',
    isNew: true, // Flag to distinguish between newly added and API-sourced taxonomies
  });
  const [taxonomyFormErrors, setTaxonomyFormErrors] = useState({});
  const [posOptions, setPosOptions] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [memberOptions, setMemberOptions] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [isCredientailing, setIsCredientailing] = useState(() => resolveIsCredentialing(initialData));

  useEffect(() => {
    if (initialData) {
      setIsCredientailing(resolveIsCredentialing(initialData));
    }
  }, [initialData]);

  // Organization.id is a UUID on the backend — never Number() it. Number("<uuid>")
  // is always NaN, which is falsy, so any `numericOrgId || null` / `numericOrgId
  // ? 'update' : 'create'` downstream would silently collapse to null/'create'.
  const parseOrgId = (res) => {
    if (!res) return null;
    if (typeof res === 'number' || typeof res === 'string') {
      return String(res);
    }
    const d = res?.data ?? res;
    const candidate =
      d?.id ??
      d?.Id ??
      d?.organization_id ??
      d?.OrganizationId ??
      (typeof d === 'number' || typeof d === 'string' ? d : null) ??
      res?.id ??
      res?.Id ??
      null;
    return candidate != null ? String(candidate) : null;
  };

  const handleSaveForm = async (values) => {
    setLoading(true);
    try {
      const storedOrgId = typeof localStorage !== 'undefined' ? localStorage.getItem('organizationId') : null;
      const effectiveOrgId =
        values.id ||
        values.organization_id ||
        organizationId ||
        initialData?.id ||
        initialData?.Id ||
        initialData?.organization_id ||
        initialData?.OrganizationId ||
        storedOrgId ||
        null;
      // Organization.id is a UUID — keep it as a string, never Number() it.
      const resolvedOrgId = effectiveOrgId ? String(effectiveOrgId) : null;

      const payload = {
        id: resolvedOrgId || null,
        Id: resolvedOrgId || null,
        organization_id: resolvedOrgId || null,
        OrganizationId: resolvedOrgId || null,
        account_id: isSuperAdmin ? values.account || null : accountId || values.account || null,
        cid: values.cid || null,
        email: values.email || null,
        status_id: values.statusId || null,
        group_office_name: values.groupOfficeName || null,
        dba: values.dba || null,
        established_on: values.establishedOn || null,
        tax_id: values.taxId || null,
        group_npi: values.groupNpi || null,
        medicaid_ptan: values.medicaidPtan || null,
        medicaid_group: values.medicaidGroup || null,
        bcbs_group: values.bcbsGroup || null,
        phone: values.phone || null,
        fax: values.fax || null,
        is_credentialing: Boolean(isCredientailing),
        mem_list: Array.isArray(values.members)
          ? values.members
            .map((member) => {
              if (member == null) return null;
              if (typeof member === 'object') return String(member.value ?? member.id ?? member.Id ?? '');
              return String(member);
            })
            .filter((value) => value !== '')
          : [],
        cred_mem: isCredientailing && values.credMembers
          ? [
            typeof values.credMembers === 'object'
              ? String(values.credMembers.value ?? values.credMembers.id ?? values.credMembers.Id ?? '')
              : String(values.credMembers),
          ].filter((value) => value !== '')
          : [],
        handled_by: isCredientailing && values.credMembers
          ? (typeof values.credMembers === 'object'
            ? String(values.credMembers.value ?? values.credMembers.id ?? values.credMembers.Id ?? '')
            : String(values.credMembers))
          : null,
        erx: !!values.erx,
        drug_injection: !!values.drugInjection,
        drug_injection_notes: values.drugInjectionNotes || null,
        labs: !!values.labs,
        emr_name: values.emrName || null,
        pm: values.pm || null,
        clearing_house: values.clearingHouse || null,
        pos_list: Array.isArray(values.pos)
          ? values.pos
            .map((p) => {
              if (p == null) return null;
              if (typeof p === 'object') return String(p.value ?? p.id ?? '');
              return String(p);
            })
            .filter((v) => v !== '')
          : [],
        avg_monthly_volume: values.avgMonthlyVolume || null,
        avg_monthly_collections: values.avgMonthlyCollections || null,
        pay_to_address_id: values.pay_to_address_id || initialData?.pay_to_address_id || initialData?.PayToAddressId || initialData?.pay_to_address?.Id || initialData?.pay_to_address?.id || null,
        mailing_address_id: values.mailing_address_id || initialData?.mailing_address_id || initialData?.MailingAddressId || initialData?.mailing_address?.Id || initialData?.mailing_address?.id || null,
        mailing_address: values.mailingAddress ? {
          ...values.mailingAddress,
          id: values.mailing_address_id || initialData?.mailing_address_id || initialData?.MailingAddressId || initialData?.mailing_address?.Id || initialData?.mailing_address?.id || null,
        } : null,
        pay_to_address: values.payToAddress ? {
          ...values.payToAddress,
          id: values.pay_to_address_id || initialData?.pay_to_address_id || initialData?.PayToAddressId || initialData?.pay_to_address?.Id || initialData?.pay_to_address?.id || null,
        } : null,
        taxonomies: Array.isArray(values.taxonomies)
          ? values.taxonomies.map((t) => ({
            id: t.id || t.Id || null,
            organization_id: resolvedOrgId || null,
            provider_id: null,
            status_id: t.statusId ?? t.status_id ?? 1,
            code: t.code || null,
            taxonomy_group: t.name || null,
            is_primary: !!t.primary,
            is_custom: !!t.custom,
            license: t.license || null,
            state: t.state || null,
            description: t.desc || null,
          }))
          : null,
        // If NPI validation provided a location_address, include it. Otherwise allow explicit form value `locationAddress` if added.
        location_address: values.locationAddress || initialData?.location_address || null,
        type: resolvedOrgId ? 'update' : 'create',
      };

      const response = await saveOrganization(payload, token);
      if (response?.is_success) {
        toast.success(response?.message || 'Organization saved successfully');
        const orgId = parseOrgId(response) || resolvedOrgId;
        if (orgId) {
          formik.setFieldValue('id', orgId);
          try {
            localStorage.setItem('organizationId', String(orgId));
          } catch (e) {
            console.error('Failed to write organizationId to localStorage', e);
          }
        }
        onSaveSuccess?.(orgId, { ...values, id: orgId });
        onContinue?.(orgId, { ...values, id: orgId });
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save organization');
      }
    } catch (error) {
      console.error('Error saving client profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const initialFormValues = React.useMemo(() => {
    const storedOrgId = typeof localStorage !== 'undefined' ? localStorage.getItem('organizationId') : null;
    const effectiveOrgId =
      initialData?.id ||
      initialData?.Id ||
      initialData?.organization_id ||
      initialData?.OrganizationId ||
      organizationId ||
      storedOrgId ||
      null;

    // Organization.id is a UUID — keep it as a string, never Number() it.
    const resolvedEffectiveId = effectiveOrgId ? String(effectiveOrgId) : null;

    if (!initialData) {
      return {
        id: resolvedEffectiveId || null,
        statusId: null,
        // Basic Information
        groupOfficeName: '',
        dba: '',
        establishedOn: '',
        phone: '',
        email: '',
        fax: '',
        members: [],
        account: isSuperAdmin ? '' : accountId || '',
        pos: [],

        // Tax & Regulatory
        cid: '',
        taxId: '',
        groupNpi: '',
        medicaidPtan: '',
        medicaidGroup: '',
        bcbsGroup: '',

        // Mailing Address
        mailingAddress: {
          street: '',
          street2: '',
          city: '',
          state: '',
          country: '',
          zipcode: '',
        },

        // Pay-to Address
        payToAddress: {
          street: '',
          street2: '',
          city: '',
          state: '',
          country: '',
          zipcode: '',
        },

        // EMR & System Information
        emrName: '',
        pm: '',
        clearingHouse: '',

        // Service Capabilities
        erx: false,
        drugInjection: false,
        drugInjectionNotes: '',
        labs: false,
        posList: [],
        avgMonthlyVolume: '',
        avgMonthlyCollections: '',

        taxonomies: [],
      };
    }

    const ma = initialData.mailing_address || initialData.MailingAddress || initialData.mailingAddress || {};
    const pa = initialData.pay_to_address || initialData.PayToAddress || initialData.payToAddress || {};

    const normalizeZip = (z) => {
      if (!z) return '';
      const s = String(z).trim();
      // If 9 digits without dash, convert to 12345-6789
      if (/^\d{9}$/.test(s)) return `${s.slice(0, 5)}-${s.slice(5)}`;
      return s;
    };

    return {
      id: resolvedEffectiveId || null,
      statusId: initialData.status_id ?? initialData.StatusId ?? initialData.statusId ?? null,
      groupOfficeName: initialData.group_office_name ?? initialData.GroupOfficeName ?? initialData.groupOfficeName ?? '',
      dba: initialData.dba ?? initialData.Dba ?? '',
      email: initialData.email ?? initialData.Email ?? initialData.group_email ?? initialData.contact_email ?? '',
      establishedOn: initialData.established_on ?? initialData.EstablishedOn ?? initialData.establishedOn ?? '',
      phone: initialData.phone ?? initialData.Phone ?? '',
      fax: initialData.fax ?? initialData.Fax ?? '',
      members: Array.isArray(initialData.mem_list)
        ? initialData.mem_list.map((member) => String(member))
        : Array.isArray(initialData.member_ids)
          ? initialData.member_ids.map((member) => String(member))
          : Array.isArray(initialData.members_ids)
            ? initialData.members_ids.map((member) => String(member))
            : Array.isArray(initialData.members)
              ? initialData.members.map((member) => String(member?.id ?? member?.Id ?? member?.member_id ?? member?.memberId ?? member))
              : [],
      is_credentialing: resolveIsCredentialing(initialData),
      credMembers: resolveCredMember(initialData),
      account: isSuperAdmin
        ? (initialData.account_id ?? initialData.AccountId ?? initialData.accountId ?? initialData.account ?? '')
        : (accountId || (initialData.account_id ?? initialData.AccountId ?? initialData.accountId ?? initialData.account ?? '')),
      pos: Array.isArray(initialData.pos_list ?? initialData.PosList ?? initialData.pos)
        ? (initialData.pos_list ?? initialData.PosList ?? initialData.pos).map((p) => String(p))
        : [],

      cid: initialData.cid ?? initialData.Cid ?? '',
      taxId: normalizeDigits(initialData.tax_id ?? initialData.TaxId ?? initialData.taxId ?? ''),
      groupNpi: normalizeDigits(initialData.group_npi ?? initialData.GroupNpi ?? initialData.groupNpi ?? initialData.npi ?? initialData.Npi ?? ''),
      medicaidPtan: initialData.medicaid_ptan ?? initialData.MedicaidPtan ?? initialData.medicaidPtan ?? '',
      medicaidGroup: initialData.medicaid_group ?? initialData.MedicaidGroup ?? initialData.medicaidGroup ?? '',
      bcbsGroup: initialData.bcbs_group ?? initialData.BcbsGroup ?? initialData.bcbsGroup ?? '',

      mailingAddress: {
        street: ma?.Street ?? ma?.street ?? '',
        street2: ma?.Street2 ?? ma?.street2 ?? '',
        city: ma?.City ?? ma?.city ?? '',
        state: ma?.State ?? ma?.state ?? '',
        country: ma?.Country ?? ma?.country ?? '',
        zipcode: normalizeZip(ma?.Zipcode ?? ma?.zipcode ?? ma?.zip ?? ma?.Zip ?? ''),
      },

      payToAddress: {
        street: pa?.Street ?? pa?.street ?? '',
        street2: pa?.Street2 ?? pa?.street2 ?? '',
        city: pa?.City ?? pa?.city ?? '',
        state: pa?.State ?? pa?.state ?? '',
        country: pa?.Country ?? pa?.country ?? '',
        zipcode: normalizeZip(pa?.Zipcode ?? pa?.zipcode ?? pa?.zip ?? pa?.Zip ?? ''),
      },

      emrName: initialData.emr_name ?? initialData.EmrName ?? initialData.emrName ?? '',
      pm: initialData.pm ?? initialData.Pm ?? '',
      clearingHouse: initialData.clearing_house ?? initialData.ClearingHouse ?? initialData.clearingHouse ?? '',

      erx: !!(initialData.erx ?? initialData.Erx),
      drugInjection: !!(initialData.drug_injection ?? initialData.DrugInjection ?? initialData.drugInjection),
      drugInjectionNotes: initialData.drug_injection_notes ?? initialData.DrugInjectionNotes ?? initialData.drugInjectionNotes ?? '',
      labs: !!(initialData.labs ?? initialData.Labs),
      posList: Array.isArray(initialData.pos_list ?? initialData.PosList ?? initialData.posList) ? (initialData.pos_list ?? initialData.PosList ?? initialData.posList) : [],
      avgMonthlyVolume: initialData.avg_monthly_volume ?? initialData.AvgMonthlyVolume ?? initialData.avgMonthlyVolume ?? '',
      avgMonthlyCollections: initialData.avg_monthly_collections ?? initialData.AvgMonthlyCollections ?? initialData.avgMonthlyCollections ?? '',

      taxonomies: Array.isArray(initialData.taxonomies ?? initialData.Taxonomies)
        ? (initialData.taxonomies ?? initialData.Taxonomies).map((t) => ({
          id: t.id ?? t.Id ?? null,
          code: t.code ?? t.Code ?? null,
          name: t.description || t.Description || t.taxonomy_group || t.TaxonomyGroup || t.code || t.Code || '',
          desc: t.description ?? t.Description ?? '',
          license: t.license ?? t.License ?? '',
          primary: !!(t.is_primary ?? t.IsPrimary ?? t.primary),
          custom: !!(t.is_custom ?? t.IsCustom ?? t.custom),
          state: t.state ?? t.State ?? '',
          statusId: t.status_id ?? t.StatusId ?? t.statusId ?? 1,
          isNew: t.id || t.Id ? false : true,
        }))
        : [],
    };
  }, [initialData, organizationId, isSuperAdmin, accountId]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: handleSaveForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Helper to flatten Formik errors for an error summary
  const getAllErrors = (errors) => {
    const out = [];
    const recurse = (obj, prefix = '') => {
      if (!obj) return;
      Object.keys(obj).forEach((key) => {
        const val = obj[key];
        if (typeof val === 'string') {
          out.push(val);
        } else if (typeof val === 'object') {
          recurse(val, `${prefix}${key}.`);
        }
      });
    };
    recurse(errors);
    return out;
  };

  const getFieldError = (fieldName) => {
    const parts = fieldName.split('.');
    let error = formik.errors;
    for (const part of parts) {
      error = error?.[part];
    }
    return error;
  };

  const getFieldTouched = (fieldName) => {
    const parts = fieldName.split('.');
    let touched = formik.touched;
    for (const part of parts) {
      touched = touched?.[part];
    }
    return touched;
  };

  const renderFieldError = (fieldName) => {
    const error = getFieldError(fieldName);
    const touched = getFieldTouched(fieldName);
    const showError = (touched || formik.submitCount > 0) && error;
    return showError ? <small className="text-danger d-block mt-1">{error}</small> : null;
  };

  const handleSameAsMailingAddress = (checked) => {
    setSameAsMailingAddress(checked);
    if (checked) {
      formik.setFieldValue('payToAddress', { ...formik.values.mailingAddress });
    }
  };

  const handleTaxonomyModalOpen = () => {
    setEditingTaxonomyIndex(null);
    setTaxonomyForm({
      code: '',
      desc: '',
      license: '',
      primary: false,
      custom: false,
      state: '',
      name: '',
      isNew: true,
    });
    setTaxonomyFormErrors({});
    setShowTaxonomyModal(true);
  };

  const handleTaxonomyModalClose = () => {
    setShowTaxonomyModal(false);
    setEditingTaxonomyIndex(null);
    setTaxonomyFormErrors({});
  };

  const handleTaxonomyEdit = (index) => {
    const taxonomyToEdit = formik.values.taxonomies[index];
    setEditingTaxonomyIndex(index);
    setTaxonomyForm({
      ...taxonomyToEdit,
      isNew: taxonomyToEdit.isNew ?? true,
    });
    setTaxonomyFormErrors({});
    setShowTaxonomyModal(true);
  };

  const validateTaxonomyForm = () => {
    const errors = {};
    if (!taxonomyForm.name.trim()) {
      errors.name = 'Name is required';
    }
    setTaxonomyFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTaxonomyFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxonomyForm({
      ...taxonomyForm,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (taxonomyFormErrors[name]) {
      setTaxonomyFormErrors({
        ...taxonomyFormErrors,
        [name]: '',
      });
    }
  };

  const handleAddTaxonomy = () => {
    if (validateTaxonomyForm()) {
      if (editingTaxonomyIndex !== null) {
        const updatedTaxonomies = [...formik.values.taxonomies];
        updatedTaxonomies[editingTaxonomyIndex] = { ...taxonomyForm };
        formik.setFieldValue('taxonomies', updatedTaxonomies);
      } else {
        formik.setFieldValue('taxonomies', [...formik.values.taxonomies, { ...taxonomyForm, isNew: true }]);
      }
      handleTaxonomyModalClose();
    }
  };

  const handleRemoveTaxonomy = async (index) => {
    const taxonomy = formik.values.taxonomies[index];

    if (!isEditMode || taxonomy.isNew) {
      formik.setFieldValue(
        'taxonomies',
        formik.values.taxonomies.filter((_, i) => i !== index)
      );
      return;
    }

    try {
      setLoading(true);

      await new Promise((resolve) => setTimeout(resolve, 300));
      formik.setFieldValue(
        'taxonomies',
        formik.values.taxonomies.filter((_, i) => i !== index)
      );
    } catch (error) {
      console.error('Error deleting taxonomy:', error);
      // toast.error('Error deleting taxonomy');
    } finally {
      setLoading(false);
    }
  };

  const renderAsterisk = () => <span className="text-danger">*</span>;

  const getPOSOptions = async () => {
    setPosLoading(true);
    try {
      const res = await getAllPOSList(token);
      if (res?.is_success) {
        const mapped = (res?.data || []).map((p) => ({ value: String(p.id), label: p.name }));
        setPosOptions(mapped);
      } else if (res?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        console.error('Server error while fetching POS options:', res);
      } else {
        console.log('Failed to fetch POS options:', res?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error fetching POS options:', error);
    } finally {
      setPosLoading(false);
    }
  };

  const loadClients = async () => {
    if (!token || !isSuperAdmin) {
      setClients([]);
      return;
    }
    try {
      setClientsLoading(true);
      const response = await getAllClientNames(token);
      if (response?.is_success) {
        const data = Array.isArray(response?.data) ? response.data : [];
        setClients(data);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setClients([]);
      } else {
        console.error('Failed to load clients:', response?.message || 'Unknown error');
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  // Credentialing/Billing Member users are internal and global (no account_id of their own),
  // so this list is every active internal member platform-wide, not scoped to this organization's
  // client -- organizationId is only passed (when known) so the backend can 404 on a bad id.
  const loadOrgMembers = React.useCallback(async (orgId) => {
    if (!token) {
      setMemberOptions([]);
      return;
    }

    try {
      setMembersLoading(true);
      const response = await getOrgMembersByOrgId(orgId, token);
      const membersData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      const mappedMembers = membersData
        .map((member) => {
          const id = member?.id ?? member?.Id ?? member?.user_id ?? member?.UserId ?? member?.member_id ?? member?.MemberId ?? member?.userId ?? member?.UserID ?? null;
          if (id == null) return null;

          const label = member?.name ?? member?.Name ?? member?.full_name ?? member?.FullName
            ?? [member?.first_name ?? member?.FirstName, member?.last_name ?? member?.LastName].filter(Boolean).join(' ')
            ?? member?.email ?? member?.Email ?? member?.user_name ?? member?.UserName
            ?? String(id);

          const userType = member?.user_type ?? member?.UserType ?? null;

          return { value: String(id), label: String(label), userType: userType != null ? Number(userType) : null };
        })
        .filter(Boolean);

      setMemberOptions(mappedMembers);
    } catch (error) {
      console.error('Error loading organization members:', error);
      setMemberOptions([]);
    } finally {
      setMembersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadClients();
    getPOSOptions();
  }, [token]);

  const activeOrgIdForMembers = organizationId ?? initialData?.id ?? formik.values.id ?? null;

  useEffect(() => {
    if (!token) return;
    loadOrgMembers(activeOrgIdForMembers);
  }, [token, activeOrgIdForMembers, loadOrgMembers]);

  const billingMemberOptions = React.useMemo(
    () => memberOptions.filter((member) => member.userType === USER_TYPE_BILLING_MEMBER),
    [memberOptions]
  );
  const credMemberOptions = React.useMemo(
    () => memberOptions.filter((member) => member.userType === USER_TYPE_CREDENTIALING_MEMBER),
    [memberOptions]
  );

  const handleAddressFieldChange = React.useCallback((fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    if (sameAsMailingAddress && fieldName.startsWith('mailingAddress.')) {
      const payToField = fieldName.replace('mailingAddress.', 'payToAddress.');
      formik.setFieldValue(payToField, value);
    }
  }, [sameAsMailingAddress, formik]);

  const clientOptions = React.useMemo(() => {
    return (clients || []).map((client) => ({
      value: String(client.Id ?? client.id),
      label: client.OrganizationName ?? client.organizationName ?? client.name ?? String(client.Id ?? client.id),
    }));
  }, [clients]);

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {/* Validation summary removed per request */}
      <div className="py-3 px-0 px-md-3">
        {/* Basic Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Basic Information</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Group Office Name {renderAsterisk()}
              </label>
              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('groupOfficeName') && getFieldError('groupOfficeName') ? 'is-invalid' : ''
                  }`}
                placeholder="e.g. Medical Center Group"
                {...formik.getFieldProps('groupOfficeName')}
              />
              {renderFieldError('groupOfficeName')}
            </div>



            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <label className="form-label fw-semibold">
                  DBA (Doing Business As)
                </label>

                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isCredientailing"
                    checked={isCredientailing}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsCredientailing(checked);
                      if (!checked) {
                        formik.setFieldValue('credMembers', '');
                      }
                    }}
                  />
                  <label
                    className="form-check-label small text-muted"
                    htmlFor="isCredientailing"
                  >
                    Is Credentialing
                  </label>
                </div>
              </div>

              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('dba') && getFieldError('dba') ? 'is-invalid' : ''
                  }`}
                placeholder="Business name"
                {...formik.getFieldProps('dba')}
              />
              {renderFieldError('dba')}
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Established On</label>

              <CustomDatePicker
                formik={formik}
                className="form-control rounded-3 shadow-sm"
                name="establishedOn"
                value={formik.values.establishedOn}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Email {renderAsterisk()}</label>
              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('email') && getFieldError('email') ? 'is-invalid' : ''
                  }`}
                placeholder="user@domain.com"
                {...formik.getFieldProps('email')}
              />
              {renderFieldError('email')}
            </div>


          </div>




          <div className="row g-3 mt-1">

            <div className="col-md-6">
              <label className="form-label fw-semibold">Phone {renderAsterisk()}</label>
              <CustomPhoneInput
                formik={formik}
                name="phone"
                placeholder="+1 (555) 123-4567"
                defaultCountry="us" />
              {/* <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('phone') && getFieldError('phone') ? 'is-invalid' : ''
                  }`}
                placeholder="+1 (555) 123-4567"
                {...formik.getFieldProps('phone')}
              /> */}
              {/* {renderFieldError('phone')} */}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Fax</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="Fax number"
                name="fax"
                value={formatIdentifierInput(formik.values.fax, 'fax')}
                onChange={(event) => formik.setFieldValue('fax', normalizeDigits(event.target.value).slice(0, 10))}
                onBlur={formik.handleBlur}
              />
            </div>

            <div className="col-md-6">
              <MultiSelectSearchableDropdown
                label="Billing Members"
                placeholder="Select members"
                options={billingMemberOptions}
                value={formik.values.members}
                onChange={(value) => formik.setFieldValue('members', value)}
                loading={membersLoading}
                disabled={false}
                required={false}
                emptyMessage={membersLoading ? 'Loading members...' : 'No members found'}
                maxVisibleTags={2}
              />
            </div>

            {isCredientailing && (
              <div className="col-md-6">
                <SearchableDropdown
                  label="Credentialing Member"
                  placeholder="Select credentialing member"
                  options={credMemberOptions}
                  value={formik.values.credMembers}
                  onChange={(value) => {
                    let selected = value;
                    if (selected && typeof selected === 'object') {
                      selected = selected.value ?? selected.id ?? selected.Id ?? selected;
                    }
                    formik.setFieldValue('credMembers', selected);
                  }}
                  loading={membersLoading}
                  disabled={false}
                  required={false}
                  emptyMessage={membersLoading ? 'Loading members...' : 'No members found'}
                />
              </div>
            )}

          </div>

          <div className="row g-3 mt-1">
            {isSuperAdmin && (
              <div className="col-md-6">
                <SearchableDropdown
                  label="Client"
                  placeholder="Select a client"
                  options={clientOptions}
                  value={formik.values.account}
                  onChange={(value) => {
                    let v = value;
                    if (v && typeof v === 'object') {
                      v = v.value ?? v.Id ?? v.id ?? null;
                    }
                    if (v === '') v = null;
                    formik.setFieldValue('account', v);
                  }}
                  disabled={clientsLoading}
                  loading={clientsLoading}
                  required={true}
                  emptyMessage="No accounts found"
                />
                {renderFieldError('account')}
              </div>
            )}

            <div className="col-md-6">
              <MultiSelectSearchableDropdown
                label="POS"
                placeholder="Select POS"
                options={posOptions}
                value={formik.values.pos}
                onChange={(value) => formik.setFieldValue('pos', value)}
                loading={posLoading}
                disabled={false}
                required={false}
                emptyMessage={posLoading ? 'Loading POS...' : 'No POS found'}
                maxVisibleTags={2}
              />
            </div>
          </div>
        </div>

        {/* Tax & Regulatory Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Tax & Regulatory Information</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">CID {renderAsterisk()}</label>
              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('cid') && getFieldError('cid') ? 'is-invalid' : ''
                  }`}
                placeholder="5-character CID"
                maxLength={5}
                name="cid"
                value={formik.values.cid}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 5);
                  formik.setFieldValue('cid', val);
                }}
                onBlur={formik.handleBlur}
              />
              {renderFieldError('cid')}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Tax ID {renderAsterisk()}</label>
              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('taxId') && getFieldError('taxId') ? 'is-invalid' : ''
                  }`}
                placeholder="EIN/Tax ID"
                name="taxId"
                value={formatIdentifierInput(formik.values.taxId, 'taxId')}
                onChange={(event) => formik.setFieldValue('taxId', normalizeDigits(event.target.value).slice(0, 9))}
                onBlur={formik.handleBlur}
              />
              {renderFieldError('taxId')}
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Group NPI {renderAsterisk()}</label>
              <input
                className={`form-control rounded-3 shadow-sm ${getFieldTouched('groupNpi') && getFieldError('groupNpi') ? 'is-invalid' : ''
                  }`}
                placeholder="10-digit NPI"
                name="groupNpi"
                value={formatIdentifierInput(formik.values.groupNpi, 'npi')}
                onChange={(event) => formik.setFieldValue('groupNpi', normalizeDigits(event.target.value).slice(0, 10))}
                onBlur={formik.handleBlur}
              />
              {renderFieldError('groupNpi')}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Medicaid PTAN</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="PTAN number"
                {...formik.getFieldProps('medicaidPtan')}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Medicaid Group</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="Medicaid group number"
                {...formik.getFieldProps('medicaidGroup')}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">BCBS Group</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="BCBS group number"
                {...formik.getFieldProps('bcbsGroup')}
              />
            </div>
          </div>
        </div>

        {/* Address Sections */}
        <div className="row g-4">
          {['mailingAddress', 'payToAddress'].map((addressType) => (
            <div className="col-md-6" key={addressType}>
              {addressType === 'payToAddress' ? (
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h6 className="fw-bold hc-text-primary m-0">
                    Pay-to Address {renderAsterisk()}
                  </h6>

                  <div className="form-check m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sameAsMailingAddress"
                      checked={sameAsMailingAddress}
                      onChange={(e) => handleSameAsMailingAddress(e.target.checked)}
                    />
                    <label
                      className="form-check-label small text-muted"
                      htmlFor="sameAsMailingAddress"
                    >
                      As Mailing Address
                    </label>
                  </div>
                </div>
              ) : (
                <h6 className="fw-bold hc-text-primary mb-3">
                  Mailing Address {renderAsterisk()}
                </h6>
              )}

              <AddressSelectorFields
                title={addressType === 'payToAddress' ? 'Pay-to Address' : 'Mailing Address'}
                fieldPrefix={addressType}
                values={formik.values[addressType]}
                errors={formik.errors[addressType] || {}}
                touched={formik.touched[addressType] || {}}
                onFieldChange={handleAddressFieldChange}
                disabled={addressType === 'payToAddress' && sameAsMailingAddress}
                renderAsterisk={renderAsterisk}
              />
            </div>
          ))}
        </div>

        {/* EMR & System Information Section */}
        <div className="mt-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">EMR & System Information</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">EMR Name</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="e.g. Epic, Cerner"
                {...formik.getFieldProps('emrName')}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Practice Management (PM)</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="PM system name"
                {...formik.getFieldProps('pm')}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Clearing House</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="Clearing house name"
                {...formik.getFieldProps('clearingHouse')}
              />
            </div>
          </div>
        </div>

        {/* Service Capabilities Section */}
        <div className="mt-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Service Capabilities</h6>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="erx"
                  {...formik.getFieldProps('erx')}
                  checked={formik.values.erx}
                />
                <label className="form-check-label fw-semibold" htmlFor="erx">
                  E-Rx Enabled
                </label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="labs"
                  {...formik.getFieldProps('labs')}
                  checked={formik.values.labs}
                />
                <label className="form-check-label fw-semibold" htmlFor="labs">
                  Labs
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="drugInjection"
                  {...formik.getFieldProps('drugInjection')}
                  checked={formik.values.drugInjection}
                />
                <label className="form-check-label fw-semibold" htmlFor="drugInjection">
                  Drug Injection
                </label>
              </div>
            </div>
          </div>

          {formik.values.drugInjection && (
            <div className="row g-3 mb-3">
              <div className="col-12">
                <label className="form-label fw-semibold">Drug Injection Notes {renderAsterisk()}</label>
                <textarea
                  className={`form-control rounded-3 shadow-sm ${getFieldTouched('drugInjectionNotes') && getFieldError('drugInjectionNotes') ? 'is-invalid' : ''
                    }`}
                  placeholder="Details about drug injection services"
                  rows="3"
                  {...formik.getFieldProps('drugInjectionNotes')}
                />
                {renderFieldError('drugInjectionNotes')}
              </div>
            </div>
          )}

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Average Monthly Volume</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="Number of claims"
                {...formik.getFieldProps('avgMonthlyVolume')}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Average Monthly Collections</label>
              <input
                className="form-control rounded-3 shadow-sm"
                placeholder="Amount in dollars"
                {...formik.getFieldProps('avgMonthlyCollections')}
              />
            </div>
          </div>
        </div>

        {/* Provider Taxonomies Section */}
        <div className="mt-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            <h6 className="fw-bold hc-text-primary m-0">Taxonomies</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-2 px-3"
              onClick={handleTaxonomyModalOpen}
            >
              + Add Taxonomy
            </button>
          </div>

          {formik.values.taxonomies.length > 0 ? (
            <div className="w-100">
              <div className="mb-3 pb-2 border-bottom">
                <h6 className="fw-semibold text-muted mb-0 text-uppercase" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                  Added Taxonomies
                </h6>
              </div>
              <div className="row g-3">
                {formik.values.taxonomies.map((taxonomy, index) => (
                  <div key={index} className="col-12 col-md-6">
                    <div className="bg-white rounded-3 border shadow-sm overflow-hidden h-100" style={{ borderLeft: taxonomy.primary ? '4px solid var(--hc-icon-blue-alt)' : '4px solid var(--hc-border-bootstrap)' }}>
                      {/* Column Headers */}
                      <div className="d-flex align-items-center p-3 border-bottom flex-wrap gap-3" style={{ backgroundColor: 'var(--hc-gray-bg-soft)' }}>
                        <div style={{ flex: '1', minWidth: '100px' }}>
                          <small className="fw-semibold text-muted">Name</small>
                        </div>
                        <div className="text-center" style={{ flex: '0 0 auto' }}>
                          <small className="fw-semibold text-muted">Is Primary</small>
                        </div>
                        <div className="text-center" style={{ flex: '0 0 auto' }}>
                          <small className="fw-semibold text-muted">Actions</small>
                        </div>
                      </div>

                      {/* Card Content - Three Columns */}
                      <div className="d-flex align-items-center p-3 flex-wrap gap-3">
                        {/* Name Column */}
                        <div style={{ flex: '1', minWidth: '100px' }}>
                          <p className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.95rem', wordBreak: 'break-word' }}>
                            {taxonomy.name}
                          </p>
                        </div>

                        {/* Primary Checkbox Column */}
                        <div className="d-flex justify-content-center align-items-center" style={{ flex: '0 0 auto' }}>
                          <div className="form-check m-0">
                            <input
                              className="form-check-input cursor-pointer"
                              type="checkbox"
                              id={`primary-${index}`}
                              checked={taxonomy.primary}
                              onChange={(e) => {
                                const updatedTaxonomies = [...formik.values.taxonomies];
                                updatedTaxonomies[index] = { ...taxonomy, primary: e.target.checked };
                                formik.setFieldValue('taxonomies', updatedTaxonomies);
                              }}
                              style={{ width: '18px', height: '18px' }}
                            />
                          </div>
                        </div>

                        {/* Actions Column */}
                        <div className="d-flex justify-content-center gap-2" style={{ flex: '0 0 auto' }}>
                          <button
                            type="button"
                            className="btn btn-sm p-2 lh-1 hc-text-primary border-0 rounded-2"
                            style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                            title="Edit"
                            onClick={() => handleTaxonomyEdit(index)}
                          >
                            <MdEdit size={18} />
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm p-2 lh-1 text-danger border-0 rounded-2"
                            style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                            title="Delete"
                            onClick={() => handleRemoveTaxonomy(index)}
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted mb-0">No taxonomies added yet. Click "Add Taxonomy" to get started.</p>
          )}
        </div>

        {/* Custom Taxonomy Section */}
        <div className="mt-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            <h6 className="fw-bold hc-text-primary m-0">Custom Taxonomy</h6>
            {formik.values.taxonomies.some((t) => t.custom) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-2 px-3"
                onClick={() => formik.setFieldValue('taxonomies', formik.values.taxonomies.map((t) => ({ ...t, custom: false })))}
              >
                Clear Selection
              </button>
            )}
          </div>

          {formik.values.taxonomies.length > 0 ? (
            <div className="row g-3">
              {formik.values.taxonomies.map((taxonomy, index) => (
                <div key={index} className="col-12 col-md-6">
                  <label
                    htmlFor={`custom-taxonomy-${index}`}
                    className="d-flex align-items-center gap-3 p-3 bg-white rounded-3 border shadow-sm h-100 cursor-pointer mb-0"
                    style={{ borderLeft: taxonomy.custom ? '4px solid var(--hc-icon-blue-alt)' : '4px solid var(--hc-border-bootstrap)' }}
                  >
                    <input
                      className="form-check-input cursor-pointer m-0"
                      type="radio"
                      name="customTaxonomy"
                      id={`custom-taxonomy-${index}`}
                      checked={!!taxonomy.custom}
                      onChange={() => formik.setFieldValue('taxonomies', formik.values.taxonomies.map((t, i) => ({ ...t, custom: i === index })))}
                      style={{ width: '18px', height: '18px', flex: '0 0 auto' }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <p className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.95rem', wordBreak: 'break-word' }}>
                        {taxonomy.name}
                      </p>
                      {taxonomy.code && <small className="text-muted">{taxonomy.code}</small>}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">Add a taxonomy above to select a custom taxonomy.</p>
          )}
        </div>

        {/* Footer Actions */}
        <div className="mt-4 d-flex flex-column flex-md-row gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-outline-secondary px-4 rounded-3"
            disabled={loading}
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>

        {/* Taxonomy Modal */}
        {showTaxonomyModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title fw-bold">
                    {editingTaxonomyIndex !== null ? 'Edit Taxonomy' : 'Add Taxonomy'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleTaxonomyModalClose}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Name {renderAsterisk()}
                      </label>
                      <input
                        type="text"
                        className={`form-control rounded-3 shadow-sm ${taxonomyFormErrors.name ? 'is-invalid' : ''
                          }`}
                        name="name"
                        placeholder="e.g. 193200000X - Multi-Specialty Group"
                        value={taxonomyForm.name}
                        onChange={handleTaxonomyFormChange}
                      />
                      {taxonomyFormErrors.name && (
                        <small className="text-danger d-block mt-1">{taxonomyFormErrors.name}</small>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Code</label>
                      <input
                        type="text"
                        className="form-control rounded-3 shadow-sm"
                        name="code"
                        placeholder="e.g. 207R00000X"
                        value={taxonomyForm.code}
                        onChange={handleTaxonomyFormChange}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Description</label>
                      <input
                        type="text"
                        className="form-control rounded-3 shadow-sm"
                        name="desc"
                        placeholder="e.g. Internal Medicine"
                        value={taxonomyForm.desc}
                        onChange={handleTaxonomyFormChange}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">License</label>
                      <input
                        type="text"
                        className="form-control rounded-3 shadow-sm"
                        name="license"
                        placeholder="e.g. A92321"
                        value={taxonomyForm.license}
                        onChange={handleTaxonomyFormChange}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">State</label>
                      <input
                        type="text"
                        className="form-control rounded-3 shadow-sm"
                        name="state"
                        placeholder="e.g. CA"
                        value={taxonomyForm.state}
                        onChange={handleTaxonomyFormChange}
                      />
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="primary"
                          id="primaryCheckbox"
                          checked={taxonomyForm.primary}
                          onChange={handleTaxonomyFormChange}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="primaryCheckbox">
                          Set as Primary
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-3"
                    onClick={handleTaxonomyModalClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary rounded-3"
                    onClick={handleAddTaxonomy}
                  >
                    {editingTaxonomyIndex !== null ? 'Update Taxonomy' : 'Add Taxonomy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
